import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config/api';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styles from './ReadMoreProject.module.css';

interface ProjectItem {
  id: number;
  title: string;
  image: string;
  image_url: string;
  description?: string;
  date?: string;
  author?: string;
  comments?: number;
}

interface LocationState {
  projectData: ProjectItem;
}

const ReadMoreProject: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState<ProjectItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const projectDataFromState = (location.state as LocationState)?.projectData;
        
        if (projectDataFromState) {
          setProject(projectDataFromState);
          setLoading(false);
          return;
        }
        
        if (!projectId) {
          throw new Error('No project ID provided');
        }
        
        console.log(`Fetching project ${projectId} from API...`);
        const response = await fetch(apiUrl(`/api/projects/${projectId}`));
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          setProject(result.data);
        } else {
          throw new Error('API returned unsuccessful response');
        }
      } catch (err) {
        setError(`Failed to load project: ${err instanceof Error ? err.message : 'Unknown error'}`);
        console.error('Error fetching project:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId, location.state]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading project details...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className={styles.container}>
        <div className={styles.notFound}>
          <h2>Project Not Found</h2>
          <p>{error || `The requested project could not be found.`}</p>
          <button onClick={() => navigate('/')} className={styles.backBtn}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.readMoreContainer}>
      <div className={styles.container}>
        <button onClick={() => navigate(-1)} className={styles.backButton}>
          <i className="fas fa-arrow-left"></i>
          Back to Projects
        </button>

        <div className={styles.projectHeader}>
          <h1 className={styles.projectTitle}>{project.title}</h1>
          <div className={styles.projectMeta}>
            {project.date && (
              <span className={styles.projectDate}>
                <i className="fas fa-calendar"></i>
                {project.date}
              </span>
            )}
            {project.author && (
              <span className={styles.projectAuthor}>
                <i className="fas fa-user"></i>
                {project.author}
              </span>
            )}
            {project.comments !== undefined && (
              <span className={styles.projectComments}>
                <i className="fas fa-comment"></i>
                Comments ({project.comments})
              </span>
            )}
          </div>
        </div>

        <div className={styles.projectImage}>
          <img 
            src={project.image_url} 
            alt={project.title}
            className={styles.mainImage}
            onError={(e) => {
              const firstLetter = project.title?.charAt(0) || 'P';
              (e.target as HTMLImageElement).src = `https://placehold.co/1200x600/2563eb/white?text=${firstLetter}`;
            }}
          />
        </div>

        <div className={styles.projectDescription}>
          <h2>About This Project</h2>
          <p>{project.description || "No description available for this project."}</p>
          
          <div className={styles.additionalInfo}>
            <h3>Project Details</h3>
            <p>
              This project represents NORA's ongoing commitment to creating positive change in Afghan society. 
              Through initiatives like "{project.title}", we strive to empower communities and build a brighter future 
              for all Afghans. Our dedicated team works tirelessly to ensure each project makes a meaningful impact 
              and contributes to sustainable development.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadMoreProject;